import { apiClient } from '../../../lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MutationConfig } from 'lib/react-query';
import { CreateGroupParams } from './types';
import { isAxiosError } from 'axios';

interface CreateGroupPromise {
  id: number;
  name: string;
}

interface CreateGroupErrorPromise {
  message: string;
}

type QueryPromise = CreateGroupPromise | CreateGroupErrorPromise;

export const createGroup = async (body: CreateGroupParams): Promise<QueryPromise> => {
  const res = await apiClient.post<QueryPromise>(`/groups`, body);
  if (isAxiosError(res)) {
    const error = new Error('axios error');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }
  return res.data;
};

interface UseCreateGroupOptions {
  config?: MutationConfig<typeof createGroup>;
}

export const useCreateGroup = ({ config }: UseCreateGroupOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['groups'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['metrics'],
        }),
      ]);

      if (config?.onSuccess) {
        config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: createGroup,
  });
};
